/**
 * Weekly Report Service — generates and sends Monday kickoff and Friday wrap reports.
 *
 * Monday 07:00: Pipeline overview for the week ahead
 * Friday 16:00: Week-in-review with stats and progress
 *
 * Sent to both Marinus and Wouter via Microsoft Graph Mail API.
 */

import { getAllFiles } from './filesApi';
import { getSettings } from './settingsApi';
import { sendEmail } from './mailApi';
import { getCurrentMonthYear, formatLongDate } from '../utils/dateHelpers';
import { calculateBonusStatus, formatRands } from '../utils/bonusCalculator';

const MARINUS_EMAIL = process.env.REACT_APP_MARINUS_EMAIL || '';
const WOUTER_EMAIL = process.env.REACT_APP_WOUTER_EMAIL || '';

/**
 * "count / bronze | count / silver | count / gold" — the Monday email's
 * tier-by-tier readout. Each later tier appears once the previous one is
 * reached. Thresholds come from the resolved milestones (settings, with
 * BONUS_THRESHOLD_DEFAULTS as the fallback), never from literals.
 */
function formatTierProgress(count, milestones) {
  const { bronze, silver, gold } = milestones;
  let text = `${count} / ${bronze.count}`;
  if (count >= bronze.count) text += ` | ${count} / ${silver.count}`;
  if (count >= silver.count) text += ` | ${count} / ${gold.count}`;
  return text;
}

/**
 * Generate and send the Monday morning kickoff report.
 *
 * @returns {Promise<void>}
 */
export async function sendMondayKickoff() {
  // Test records (is_test = true, CLAUDE.md §33) never appear in reports.
  // Settings supply the bonus thresholds and amounts (§10.1).
  const [allFiles, settings] = await Promise.all([getAllFiles(), getSettings()]);
  const files = allFiles.filter((f) => f.isTest !== true);
  const currentMonth = getCurrentMonthYear();
  const monthFiles = files.filter((f) => f.monthYear === currentMonth);

  const inboxCount = files.filter((f) => f.status === 'submitted').length;
  const inProgressCount = files.filter((f) => f.status === 'in_progress').length;
  const awaitingSignCount = files.filter((f) => f.status === 'sent_to_sign').length;
  const completedThisMonth = monthFiles.filter((f) => f.status === 'completed').length;

  // Wouter's bonus qualifying count
  const wouterQualifying = monthFiles.filter(
    (f) => f.qualifiesForBonus && f.drawnBy === WOUTER_EMAIL
  ).length;
  const bonus = calculateBonusStatus(wouterQualifying, settings);

  // Build visual progress from the resolved thresholds
  const bonusStatus = formatTierProgress(wouterQualifying, bonus.milestones);

  const dateStr = formatLongDate(new Date());

  const subject = `Inbox Weekly Kickoff — Week of ${dateStr}`;

  const body = `
    <div style="font-family: 'DM Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #1A2B3C;">
      <div style="background: linear-gradient(135deg, #0F4C75, #1B9AAA); padding: 24px; border-radius: 12px 12px 0 0;">
        <h2 style="color: white; margin: 0; font-size: 20px;">📊 Weekly Kickoff</h2>
        <p style="color: rgba(255,255,255,0.8); margin: 4px 0 0; font-size: 14px;">Week of ${dateStr}</p>
      </div>
      <div style="background: white; padding: 24px; border: 1px solid #D0DAE6; border-top: none; border-radius: 0 0 12px 12px;">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">Files in inbox (undrawn)</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: ${inboxCount > 0 ? '#1B9AAA' : '#2EC4B6'};">${inboxCount}</strong>
            </td>
          </tr>
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">Files in progress</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: #0F4C75;">${inProgressCount}</strong>
            </td>
          </tr>
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">Files awaiting signature</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: #F4A261;">${awaitingSignCount}</strong>
            </td>
          </tr>
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">Completed this month</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: #2EC4B6;">${completedThisMonth}</strong>
            </td>
          </tr>
          <tr>
            <td style="padding: 10px 0;">
              <span style="color: #6B7A8D; font-size: 13px;">Bonus status</span>
            </td>
            <td style="padding: 10px 0; text-align: right;">
              <strong style="font-size: 14px; color: #F4A261;">${bonusStatus}</strong>
            </td>
          </tr>
        </table>

        ${bonus.nextTier ? `
          <div style="background: #F0F4F8; border-radius: 8px; padding: 12px; margin-bottom: 16px;">
            <div style="font-size: 12px; color: #6B7A8D; margin-bottom: 6px;">Bonus Progress — ${bonus.nextTier.label} (${formatRands(bonus.nextTier.amount)})</div>
            <div style="background: #E8EFF5; border-radius: 999px; height: 8px; overflow: hidden;">
              <div style="background: linear-gradient(90deg, #2EC4B6, #1B9AAA); height: 100%; width: ${bonus.progressToNext}%; border-radius: 999px;"></div>
            </div>
            <div style="font-size: 12px; color: #6B7A8D; margin-top: 4px; text-align: right;">${bonus.filesUntilNext} files to go</div>
          </div>
        ` : ''}

        <p style="color: #1A2B3C; font-size: 15px; margin-top: 16px;">
          Have a great week — let's make it count. 💪
        </p>

        <hr style="border: none; border-top: 1px solid #D0DAE6; margin: 20px 0;" />
        <p style="color: #6B7A8D; font-size: 12px;">Inbox © 2025 — Private & Confidential</p>
      </div>
    </div>
  `;

  const recipients = [MARINUS_EMAIL, WOUTER_EMAIL].filter(Boolean);
  await Promise.all(
    recipients.map((to) => sendEmail({ to, subject, body }))
  );
}

/**
 * Generate and send the Friday afternoon wrap report.
 *
 * @returns {Promise<void>}
 */
export async function sendFridayWrap() {
  // Test records (is_test = true, CLAUDE.md §33) never appear in reports.
  // Settings supply the bonus thresholds and amounts (§10.1).
  const [allFiles, settings] = await Promise.all([getAllFiles(), getSettings()]);
  const files = allFiles.filter((f) => f.isTest !== true);
  const currentMonth = getCurrentMonthYear();
  const monthFiles = files.filter((f) => f.monthYear === currentMonth);

  // Calculate this week's stats (Mon–Fri)
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - now.getDay() + 1);
  monday.setHours(0, 0, 0, 0);

  const weekFiles = files.filter((f) => {
    if (f.status !== 'completed' || !f.completedAt) return false;
    return new Date(f.completedAt) >= monday;
  });

  const completedThisWeek = weekFiles.length;

  // Average processing time this week
  const avgProcessingTime = weekFiles.length > 0
    ? (weekFiles.reduce((sum, f) => sum + (f.activeDaysUsed || 0), 0) / weekFiles.length).toFixed(1)
    : '—';

  // SLA compliance this week
  const weekSLACompliant = weekFiles.filter((f) => !f.slaBreached).length;
  const slaComplianceRate = weekFiles.length > 0
    ? Math.round((weekSLACompliant / weekFiles.length) * 100)
    : 100;

  // Running monthly totals
  const monthCompleted = monthFiles.filter((f) => f.status === 'completed').length;
  const wouterQualifying = monthFiles.filter(
    (f) => f.qualifiesForBonus && f.drawnBy === WOUTER_EMAIL
  ).length;
  const bonus = calculateBonusStatus(wouterQualifying, settings);

  // Visual text progress bar
  const progressBarLength = 20;
  const filledLength = Math.round((bonus.progressToNext / 100) * progressBarLength);
  const progressBar = '█'.repeat(filledLength) + '░'.repeat(progressBarLength - filledLength);

  const dateStr = formatLongDate(new Date());

  const subject = `Inbox Weekly Wrap — ${dateStr}`;

  const body = `
    <div style="font-family: 'DM Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #1A2B3C;">
      <div style="background: linear-gradient(135deg, #0F4C75, #1B9AAA); padding: 24px; border-radius: 12px 12px 0 0;">
        <h2 style="color: white; margin: 0; font-size: 20px;">📋 Weekly Wrap</h2>
        <p style="color: rgba(255,255,255,0.8); margin: 4px 0 0; font-size: 14px;">${dateStr}</p>
      </div>
      <div style="background: white; padding: 24px; border: 1px solid #D0DAE6; border-top: none; border-radius: 0 0 12px 12px;">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">Files completed this week</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: #2EC4B6;">${completedThisWeek}</strong>
            </td>
          </tr>
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">Average processing time</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: #0F4C75;">${avgProcessingTime} days</strong>
            </td>
          </tr>
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">SLA compliance rate</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: ${slaComplianceRate >= 90 ? '#2EC4B6' : slaComplianceRate >= 70 ? '#F4A261' : '#C0392B'};">${slaComplianceRate}%</strong>
            </td>
          </tr>
          <tr>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5;">
              <span style="color: #6B7A8D; font-size: 13px;">Running monthly total</span>
            </td>
            <td style="padding: 10px 0; border-bottom: 1px solid #E8EFF5; text-align: right;">
              <strong style="font-size: 18px; color: #0F4C75;">${monthCompleted} files</strong>
            </td>
          </tr>
        </table>

        <div style="background: #F0F4F8; border-radius: 8px; padding: 12px; margin-bottom: 16px;">
          <div style="font-size: 12px; color: #6B7A8D; margin-bottom: 6px;">
            Bonus Progress: ${wouterQualifying} qualifying files
          </div>
          <div style="font-family: monospace; font-size: 14px; color: #0F4C75; letter-spacing: 1px;">
            ${progressBar} ${bonus.progressToNext.toFixed(0)}%
          </div>
          <div style="font-size: 11px; color: #6B7A8D; margin-top: 4px;">
            🥉 ${bonus.milestones.bronze.count} &nbsp; 🥈 ${bonus.milestones.silver.count} &nbsp; 🥇 ${bonus.milestones.gold.count}
          </div>
        </div>

        <p style="color: #1A2B3C; font-size: 15px; margin-top: 16px;">
          Well done on this week's work. See you Monday. 🙌
        </p>

        <hr style="border: none; border-top: 1px solid #D0DAE6; margin: 20px 0;" />
        <p style="color: #6B7A8D; font-size: 12px;">Inbox © 2025 — Private & Confidential</p>
      </div>
    </div>
  `;

  const recipients = [MARINUS_EMAIL, WOUTER_EMAIL].filter(Boolean);
  await Promise.all(
    recipients.map((to) => sendEmail({ to, subject, body }))
  );
}

/**
 * Determine which report(s) should be sent right now, based on
 * the current day and time.
 *
 * @returns {'monday' | 'friday' | null}
 */
export function getScheduledReport() {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();

  // Monday between 06:45 and 07:15 (allows some scheduling slack)
  if (day === 1 && hour >= 6 && hour <= 7) return 'monday';

  // Friday between 15:45 and 16:15
  if (day === 5 && hour >= 15 && hour <= 16) return 'friday';

  return null;
}
